import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  query,
  where,
  orderBy,
  limit,
  arrayUnion,
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { Client, ClientFormData, Case, CaseFormData, Payment, Note, Document } from '../types';
import { firestore, storage } from '../config/firebase';

// Helper function to check authentication
const checkAuth = () => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User not authenticated');
  }
  return auth.currentUser;
};

// Client Operations
export const addClient = async (clientData: ClientFormData): Promise<string> => {
  try {
    const user = checkAuth();
    const clientRef = await addDoc(collection(firestore, 'clients'), {
      ...clientData,
      cases: [],
      createdBy: user.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    if (clientData.initialCase) {
      await addCase(clientRef.id, clientData.initialCase);
    }

    return clientRef.id;
  } catch (error) {
    console.error('Error adding client:', error);
    throw error;
  }
};

export const getAllClients = async (
  includeArchived: boolean = false,
  ownerUid?: string,
): Promise<Client[]> => {
  try {
    const user = checkAuth();
    console.log('Current user:', user.uid, 'Email:', user.email);

    // If the caller supplies an ownerUid (typically a staff user fetching
    // only their own clients), filter server-side. This matches the
    // ownership rules — a staff user has no read permission on docs they
    // don't own, so an unfiltered query would 403.
    const clientsRef = collection(firestore, 'clients');
    const clientsSnapshot = ownerUid
      ? await getDocs(query(clientsRef, where('createdBy', '==', ownerUid)))
      : await getDocs(clientsRef);
    
    console.log('Successfully fetched clients. Count:', clientsSnapshot.size);
    
    // Create an array to store all promises
    const clientPromises = clientsSnapshot.docs.map(async (clientDoc) => {
      const clientData = clientDoc.data();
      const clientId = clientDoc.id;
      
      try {
        // Fetch cases for each client individually
        console.log(`Fetching cases for client: ${clientId}`);
        const casesRef = collection(firestore, 'cases');
        const casesQuery = query(casesRef, where('clientId', '==', clientId));
        
        const casesSnapshot = await getDocs(casesQuery);
        console.log(`Successfully fetched ${casesSnapshot.size} cases for client: ${clientId}`);
        
        const cases = casesSnapshot.docs.map(caseDoc => {
          const caseData = caseDoc.data();
          // Ensure all required fields have default values
          return {
            id: caseDoc.id,
            clientId: clientId,
            type: caseData.type || 'Unknown',
            applicationDate: caseData.applicationDate || new Date().toISOString(),
            receiptNumber: caseData.receiptNumber || '',
            amountCharged: Number(caseData.amountCharged) || 0,
            depositPaid: Number(caseData.depositPaid) || 0,
            status: caseData.status || 'Active',
            biometricTaken: caseData.biometricTaken || false,
            biometricDate: caseData.biometricDate || null,
            workPermitEligible: caseData.workPermitEligible || false,
            daysUntilWorkPermit: caseData.daysUntilWorkPermit || null,
            payments: Array.isArray(caseData.payments) ? caseData.payments : [],
            notes: Array.isArray(caseData.notes) ? caseData.notes : [],
            documents: Array.isArray(caseData.documents) ? caseData.documents : [],
            createdAt: caseData.createdAt || Timestamp.now(),
            updatedAt: caseData.updatedAt || Timestamp.now()
          } as Case;
        });
        
        return {
          id: clientId,
          fullName: clientData.fullName,
          email: clientData.email,
          phoneNumber: clientData.phoneNumber,
          countryOfOrigin: clientData.countryOfOrigin,
          createdBy: clientData.createdBy || null,
          isArchived: clientData.isArchived || false,
          archivedAt: clientData.archivedAt || null,
          archiveReason: clientData.archiveReason || null,
          createdAt: clientData.createdAt || Timestamp.now(),
          updatedAt: clientData.updatedAt || Timestamp.now(),
          cases: cases
        } as Client;
      } catch (caseError) {
        console.error(`Error fetching cases for client ${clientId}:`, caseError);
        // Return client with empty cases array if case fetching fails
        return {
          id: clientId,
          fullName: clientData.fullName,
          email: clientData.email,
          phoneNumber: clientData.phoneNumber,
          countryOfOrigin: clientData.countryOfOrigin,
          createdBy: clientData.createdBy || null,
          isArchived: clientData.isArchived || false,
          archivedAt: clientData.archivedAt || null,
          archiveReason: clientData.archiveReason || null,
          createdAt: clientData.createdAt || Timestamp.now(),
          updatedAt: clientData.updatedAt || Timestamp.now(),
          cases: []
        } as Client;
      }
    });
    
    // Wait for all promises to resolve
    const allClients = await Promise.all(clientPromises);
    
    // Filter out archived clients if not requested
    const clients = includeArchived 
      ? allClients 
      : allClients.filter(client => client.isArchived !== true);
    
    console.log('Successfully processed all clients:', allClients.length);
    console.log('Returning clients (after archive filter):', clients.length);
    return clients;
    
  } catch (error) {
    console.error('Error getting clients:', error);
    if (error instanceof Error) {
      if (error.message === 'User not authenticated') {
        console.error('Authentication error: User is not signed in');
        throw new Error('Please sign in to access client data');
      }
      console.error('Detailed error:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
    }
    throw new Error('Failed to fetch clients. Please try again.');
  }
};

export const getClient = async (id: string): Promise<Client> => {
  try {
    checkAuth();
    const clientDoc = await getDoc(doc(firestore, 'clients', id));
    
    if (!clientDoc.exists()) {
      throw new Error('Client not found');
    }

    const clientData = clientDoc.data();
    
    // Fetch cases for the client
    const casesQuery = query(
      collection(firestore, 'cases'),
      where('clientId', '==', id),
      orderBy('createdAt', 'desc')
    );
    const casesSnapshot = await getDocs(casesQuery);
    const cases = casesSnapshot.docs.map(caseDoc => ({
      id: caseDoc.id,
      ...caseDoc.data(),
    })) as Case[];

    return {
      id: clientDoc.id,
      ...clientData,
      cases,
    } as Client;
  } catch (error) {
    console.error('Error getting client:', error);
    throw error;
  }
};

export const updateClient = async (
  id: string,
  data: Partial<Client>
): Promise<Client> => {
  try {
    checkAuth();
    const docRef = doc(firestore, 'clients', id);
    
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
    
    return await getClient(id);
  } catch (error) {
    console.error('Error updating client:', error);
    throw error;
  }
};

export const deleteClient = async (id: string): Promise<void> => {
  try {
    checkAuth();
    // Get all cases for the client
    const casesQuery = query(
      collection(firestore, 'cases'),
      where('clientId', '==', id)
    );
    const casesSnapshot = await getDocs(casesQuery);

    // Delete all cases and their associated documents
    await Promise.all(
      casesSnapshot.docs.map(async (caseDoc) => {
        const caseData = caseDoc.data();
        if (caseData.documents) {
          await Promise.all(
            caseData.documents.map((doc: Document) =>
              deleteDocument(id, caseDoc.id, doc.path)
            )
          );
        }
        await deleteDoc(doc(firestore, 'cases', caseDoc.id));
      })
    );

    // Delete the client
    await deleteDoc(doc(firestore, 'clients', id));
  } catch (error) {
    console.error('Error deleting client:', error);
    throw error;
  }
};

// Case Operations
export const addCase = async (clientId: string, caseData: CaseFormData): Promise<string> => {
  try {
    const user = checkAuth();
    const caseRef = await addDoc(collection(firestore, 'cases'), {
      clientId,
      type: caseData.type,
      applicationDate: caseData.applicationDate,
      receiptNumber: caseData.receiptNumber,
      amountCharged: caseData.amountCharged,
      status: 'Active',
      payments: [],
      notes: [],
      documents: [],
      createdBy: user.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Update client document with the new case reference
    const clientRef = doc(firestore, 'clients', clientId);
    await updateDoc(clientRef, {
      cases: arrayUnion(caseRef.id),
      updatedAt: serverTimestamp(),
    });

    return caseRef.id;
  } catch (error) {
    console.error('Error adding case:', error);
    throw error;
  }
};

export const getCase = async (caseId: string): Promise<Case> => {
  try {
    checkAuth();
    const caseDoc = await getDoc(doc(firestore, 'cases', caseId));
    if (!caseDoc.exists()) {
      throw new Error('Case not found');
    }
    return { id: caseDoc.id, ...caseDoc.data() } as Case;
  } catch (error) {
    console.error('Error getting case:', error);
    throw error;
  }
};

export const updateCase = async (
  caseId: string,
  caseData: Partial<Case>
): Promise<void> => {
  try {
    checkAuth();
    const caseRef = doc(firestore, 'cases', caseId);
    await updateDoc(caseRef, {
      ...caseData,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating case:', error);
    throw error;
  }
};

// Case amount update operation
export const updateCaseAmount = async (caseId: string, newAmount: number, newTotalPaid: number): Promise<void> => {
  try {
    const caseRef = doc(firestore, 'cases', caseId);
    const caseDoc = await getDoc(caseRef);
    
    if (!caseDoc.exists()) {
      throw new Error('Case not found');
    }

    const caseData = caseDoc.data();
    const currentTotalPaid = caseData.payments.reduce((sum: number, payment: any) => sum + (Number(payment.amount) || 0), 0);
    const difference = newTotalPaid - currentTotalPaid;

    // If there's a difference in total paid, add a new payment record
    if (difference !== 0) {
      const newPayment = {
        id: `payment_${Date.now()}`,
        amount: difference,
        date: new Date().toISOString().split('T')[0],
        caseId,
        notes: 'Amount adjustment'
      };

      await updateDoc(caseRef, {
        amountCharged: newAmount,
        payments: arrayUnion(newPayment),
        updatedAt: serverTimestamp(),
      });
    } else {
      // Just update the amount charged
      await updateDoc(caseRef, {
        amountCharged: newAmount,
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Error updating case amount:', error);
    throw error;
  }
};

// Payment Operations
export const addPayment = async (caseId: string, payment: Payment): Promise<void> => {
  try {
    checkAuth();
    const caseRef = doc(firestore, 'cases', caseId);
    const caseDoc = await getDoc(caseRef);
    
    if (!caseDoc.exists()) {
      throw new Error('Case not found');
    }

    const caseData = caseDoc.data();
    const updatedPayments = [...(caseData.payments || []), payment];
    const totalPaid = updatedPayments.reduce((sum, p) => sum + p.amount, 0);
    
    await updateDoc(caseRef, {
      payments: updatedPayments,
      balance: caseData.amountCharged - totalPaid,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error adding payment:', error);
    throw error;
  }
};

// Payment update operation
export const updatePayment = async (caseId: string, paymentId: string, newAmount: number): Promise<void> => {
  try {
    checkAuth();
    const caseRef = doc(firestore, 'cases', caseId);
    const caseDoc = await getDoc(caseRef);
    
    if (!caseDoc.exists()) {
      throw new Error('Case not found');
    }

    const caseData = caseDoc.data();
    const payments = [...(caseData.payments || [])];
    const paymentIndex = payments.findIndex(p => p.id === paymentId);
    
    if (paymentIndex === -1) {
      throw new Error('Payment not found');
    }

    // Update the payment amount
    payments[paymentIndex] = {
      ...payments[paymentIndex],
      amount: newAmount
    };

    // Recalculate total paid and balance
    const totalPaid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    
    await updateDoc(caseRef, {
      payments: payments,
      balance: caseData.amountCharged - totalPaid,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating payment:', error);
    throw error;
  }
};

// Note Operations
export const addNote = async (caseId: string, content: string): Promise<string> => {
  try {
    checkAuth();
    const caseRef = doc(firestore, 'cases', caseId);
    const note: Note = {
      id: Date.now().toString(),
      content,
      timestamp: new Date().toISOString(),
      caseId,
    };

    await updateDoc(caseRef, {
      notes: arrayUnion(note),
      updatedAt: serverTimestamp(),
    });

    return note.id;
  } catch (error) {
    console.error('Error adding note:', error);
    throw error;
  }
};

// Document Operations
export const uploadDocument = async (
  clientId: string,
  caseId: string,
  file: File
): Promise<string> => {
  try {
    checkAuth();
    const timestamp = Date.now();
    const fileName = `${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}_${timestamp}`;
    const storageRef = ref(storage, `documents/${clientId}/${caseId}/${fileName}`);
    
    // Add required metadata
    const metadata = {
      contentType: file.type,
      customMetadata: {
        fileName: file.name,
        timestamp: timestamp.toString(),
        caseId: caseId,
        clientId: clientId,
      },
    };
    
    await uploadBytes(storageRef, file, metadata);
    const downloadURL = await getDownloadURL(storageRef);
    
    // Add document reference to case
    const caseRef = doc(firestore, 'cases', caseId);
    const document: Document = {
      id: `doc_${timestamp}`,
      name: fileName,
      type: file.type,
      size: file.size,
      url: downloadURL,
      path: `documents/${clientId}/${caseId}/${fileName}`,
      createdAt: Timestamp.now(),
    };

    await updateDoc(caseRef, {
      documents: arrayUnion(document),
      updatedAt: serverTimestamp(),
    });

    return downloadURL;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw error;
  }
};

export const deleteDocument = async (
  clientId: string,
  caseId: string,
  filePath: string
): Promise<void> => {
  try {
    checkAuth();
    // Delete the file from Storage
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);

    // Remove document reference from case
    const caseRef = doc(firestore, 'cases', caseId);
    const caseDoc = await getDoc(caseRef);
    
    if (caseDoc.exists()) {
      const caseData = caseDoc.data();
      const updatedDocuments = caseData.documents.filter(
        (doc: Document) => doc.path !== filePath
      );
      
      await updateDoc(caseRef, {
        documents: updatedDocuments,
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

export const handleDownload = async (url: string): Promise<void> => {
  try {
    checkAuth();
    const response = await fetch(url, {
      headers: {
        'Origin': window.location.origin,
        'Access-Control-Request-Method': 'GET',
        'Access-Control-Request-Headers': '*',
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to download file');
    }
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = url.split('/').pop() || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

export const cleanupDuplicatePayments = async (caseId: string): Promise<void> => {
  try {
    const caseRef = doc(firestore, 'cases', caseId);
    const caseDoc = await getDoc(caseRef);

    if (!caseDoc.exists()) {
      throw new Error('Case not found');
    }

    const caseData = caseDoc.data() as Case;
    const payments = caseData.payments || [];

    // Create a map to store unique payments by date
    const uniquePayments = new Map<string, Payment>();

    // Keep only the latest payment for each date
    payments.forEach(payment => {
      const existingPayment = uniquePayments.get(payment.date);
      if (!existingPayment || new Date(payment.id.split('_')[1]).getTime() > new Date(existingPayment.id.split('_')[1]).getTime()) {
        uniquePayments.set(payment.date, payment);
      }
    });

    // Convert map back to array and sort by date
    const cleanedPayments = Array.from(uniquePayments.values())
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Update the case with cleaned payments
    await updateDoc(caseRef, {
      payments: cleanedPayments
    });

  } catch (error) {
    console.error('Error cleaning up duplicate payments:', error);
    throw error;
  }
};

// Archive Operations
export const archiveClient = async (
  clientId: string, 
  reason: 'cases_closed' | 'balance_cleared' | 'manual' | 'inactive'
): Promise<void> => {
  try {
    checkAuth();
    const clientRef = doc(firestore, 'clients', clientId);
    
    await updateDoc(clientRef, {
      isArchived: true,
      archivedAt: serverTimestamp(),
      archiveReason: reason,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error archiving client:', error);
    throw error;
  }
};

export const unarchiveClient = async (clientId: string): Promise<void> => {
  try {
    checkAuth();
    const clientRef = doc(firestore, 'clients', clientId);
    
    await updateDoc(clientRef, {
      isArchived: false,
      archivedAt: null,
      archiveReason: null,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error unarchiving client:', error);
    throw error;
  }
};

export const getArchivedClients = async (
  ownerUid?: string,
): Promise<Client[]> => {
  try {
    const user = checkAuth();
    console.log('Fetching archived clients for user:', user.uid);

    // Mirrors getAllClients: when an ownerUid is supplied, server-side
    // filter by createdBy so the rules don't 403 the unfiltered list.
    const clientsRef = collection(firestore, 'clients');
    const archivedQuery = ownerUid
      ? query(
          clientsRef,
          where('isArchived', '==', true),
          where('createdBy', '==', ownerUid),
        )
      : query(clientsRef, where('isArchived', '==', true));
    const clientsSnapshot = await getDocs(archivedQuery);
    
    const clientPromises = clientsSnapshot.docs.map(async (clientDoc) => {
      const clientData = clientDoc.data();
      const clientId = clientDoc.id;
      
      // Fetch cases for each client
      const casesRef = collection(firestore, 'cases');
      const casesQuery = query(casesRef, where('clientId', '==', clientId));
      const casesSnapshot = await getDocs(casesQuery);
      
      const cases = casesSnapshot.docs.map(caseDoc => {
        const caseData = caseDoc.data();
        return {
          id: caseDoc.id,
          clientId: clientId,
          type: caseData.type || 'Unknown',
          applicationDate: caseData.applicationDate || new Date().toISOString(),
          receiptNumber: caseData.receiptNumber || '',
          amountCharged: Number(caseData.amountCharged) || 0,
          status: caseData.status || 'Active',
          biometricTaken: caseData.biometricTaken || false,
          biometricDate: caseData.biometricDate || null,
          workPermitEligible: caseData.workPermitEligible || false,
          daysUntilWorkPermit: caseData.daysUntilWorkPermit || null,
          payments: Array.isArray(caseData.payments) ? caseData.payments : [],
          notes: Array.isArray(caseData.notes) ? caseData.notes : [],
          documents: Array.isArray(caseData.documents) ? caseData.documents : [],
          createdAt: caseData.createdAt || serverTimestamp(),
          updatedAt: caseData.updatedAt || serverTimestamp(),
        } as Case;
      });
      
      return {
        id: clientId,
        fullName: clientData.fullName || '',
        email: clientData.email || '',
        phoneNumber: clientData.phoneNumber || '',
        countryOfOrigin: clientData.countryOfOrigin || '',
        cases: cases,
        isArchived: clientData.isArchived || false,
        archivedAt: clientData.archivedAt || null,
        archiveReason: clientData.archiveReason || null,
        createdAt: clientData.createdAt || serverTimestamp(),
        updatedAt: clientData.updatedAt || serverTimestamp(),
      } as Client;
    });
    
    return await Promise.all(clientPromises);
  } catch (error) {
    console.error('Error fetching archived clients:', error);
    throw error;
  }
};

 